// QuizContent is a React component that accepts a filter argument to customize the fetching of quiz questions from a backend service.
//   It also offers a dialog for confirming submissions when questions are unanswered.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { shuffleArray } from '../utils/answerMatching';
import { TextField, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { QuestionReportButton, QuizComplete, MathSymbolKeyboard, MathRenderer } from '../components';
import { QuestionImageDisplay } from '../components';
import MathLiveField from './MathLiveField';
import axios from 'axios';
import { API_BASE_URL } from "../config";
import useAutoGrowTextarea from '../utils/useAutoGrowTextarea';

const studentInfo = JSON.parse(localStorage.getItem("user") || "{}");
const studentId = studentInfo?.id;

const useStyles = makeStyles((theme) => ({
  button: {
    width: '100%',
    maxWidth: 720,
    margin: '8px 0',
    display: 'block',
    color: 'white',
    fontWeight: '100',
    borderColor: 'white',
    transition: 'background 1s',
    textTransform: 'none',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    '&:hover': {
      background: 'white',
      color: 'black',
    },
    [theme.breakpoints.up('sm')]: {
      width: '85%',
      margin: '8px auto',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },

  buttonSelect: {
    width: '100%',
    maxWidth: 720,
    margin: '8px 0',
    display: 'block',
    fontWeight: '100',
    borderColor: 'white',
    background: 'white',
    color: 'black',
    transition: 'background 1s',
    textTransform: 'none',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    '&:hover': {
      background: 'white',
      color: 'black',
    },
    [theme.breakpoints.up('sm')]: {
      width: '85%',
      margin: '8px auto',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
}));

const QuizContent = ({ filter }) => {

  const [currentQuestionIndex, setcurrentQuestionIndex] = useState(0);
  const [questionsFromDatabase, setQuestionsFromDatabase] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {};

        if (filter.subjectId) params.subjectId = filter.subjectId;
        if (filter.year) params.year = filter.year;
        if (filter.questionType) params.questionType = filter.questionType;
        if (filter.module) params.module = filter.module;

        if (filter.random > 0) params.random = filter.random;

        const response = await axios.get(`${API_BASE_URL}/questions`, { params });

        // Shuffle at the group level so sub-parts stay together
        const shuffleGroups = (qs) => {
          const groups = [];
          const parentMap = {};
          qs.forEach((q) => {
            const p = q.parent_question_number;
            if (p) {
              if (parentMap[p] !== undefined) { groups[parentMap[p]].push(q); }
              else { parentMap[p] = groups.length; groups.push([q]); }
            } else { groups.push([q]); }
          });
          for (let i = groups.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [groups[i], groups[j]] = [groups[j], groups[i]];
          }
          return groups.flat();
        };

        const shuffled = shuffleGroups(response.data);
        // Fisher-Yates, not sort(() => Math.random() - 0.5): that comparator is not a
        // shuffle and leaves the second option in position 2 or 3 about 76% of the time,
        // which lets a student learn where the answer sits instead of the subject.
        const withShuffledOptions = shuffled.map((question) => ({
          ...question,
          options: question.options ? shuffleArray(question.options) : question.options,
        }));

        setQuestionsFromDatabase(withShuffledOptions);
      } catch (error) {
        console.error('Error fetching data:', error);
        setQuestionsFromDatabase([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter]);

  // ── Group questions by parent_question_number ──────────────────────────────
  const groups = useMemo(() => {
    const result = [];
    const parentMap = {};
    questionsFromDatabase.forEach((q, idx) => {
      const parent = q.parent_question_number;
      if (parent) {
        if (parentMap[parent] !== undefined) {
          result[parentMap[parent]].indices.push(idx);
        } else {
          parentMap[parent] = result.length;
          result.push({ parent, indices: [idx], isMultiPart: true });
        }
      } else {
        result.push({ parent: null, indices: [idx], isMultiPart: false });
      }
    });
    // Sort sub-parts within each group by question_number
    result.forEach((g) => {
      if (g.isMultiPart) {
        g.indices.sort((a, b) =>
          (questionsFromDatabase[a].question_number ?? '').localeCompare(
            questionsFromDatabase[b].question_number ?? '', undefined, { numeric: true }
          )
        );
      }
    });
    return result;
  }, [questionsFromDatabase]);

  // currentQuestionIndex points to the first flat index of the current group
  const currentGroupIdx = useMemo(
    () => Math.max(0, groups.findIndex((g) => g.indices.includes(currentQuestionIndex))),
    [groups, currentQuestionIndex]
  );
  const currentGroup = groups[currentGroupIdx] ?? { parent: null, indices: [currentQuestionIndex], isMultiPart: false };

  const [openWarning, setOpenWarning] = useState(false);
  const handleCloseWarning = () => setOpenWarning(false);

  const classes = useStyles();
  const [userAnswers, setUserAnswers] = useState({});

  const inputRef = useRef(null);

  // Only the extended-response answer box grows with what's typed; short answers and
  // maths keep their single-line field. It shares inputRef so the maths symbol keyboard
  // can still place the caret — the hook ignores anything that isn't a textarea.
  const activeQuestion = questionsFromDatabase?.[currentQuestionIndex];
  const answerBoxRef = useAutoGrowTextarea(
    userAnswers?.[activeQuestion?.id] ?? '',
    { ref: inputRef, minHeight: 96 }
  );
  const mathFieldRefs = useRef({});
  const blankFieldRefs = useRef({});
  const activeBlankKeyRef = useRef(null);

  const handleAnswer = (questionId, answer) => {
    setUserAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer ?? "",
    }));
  };

  const isAnswerComplete = (question, answer) => {
    if (question.question_type === 'fill_in_the_blank') {
      return (
        Array.isArray(answer) &&
        answer.length > 0 &&
        answer.every((value) => String(value ?? '').trim() !== '')
      );
    }

    return String(answer ?? '').trim() !== '';
  };

  const handleNext = () => {
    // Mark all questions in current group as answered if not yet recorded
    currentGroup.indices.forEach((idx) => {
      const id = questionsFromDatabase[idx].id;
      if (!(id in userAnswers)) {
        setUserAnswers((prev) => ({ ...prev, [id]: "" }));
      }
    });

    if (currentGroupIdx < groups.length - 1) {
      setcurrentQuestionIndex(groups[currentGroupIdx + 1].indices[0]);
    }
  };

  const handleSubmit = () => {
    const unansweredQuestions = questionsFromDatabase.filter(
      (question) => !isAnswerComplete(question, userAnswers[question.id])
    );

    if (unansweredQuestions.length > 0) {
      setOpenWarning(true);
      return;
    }

    // Mark current group questions as answered
    currentGroup.indices.forEach((idx) => {
      const id = questionsFromDatabase[idx].id;
      if (!(id in userAnswers)) {
        setUserAnswers((prev) => ({ ...prev, [id]: "" }));
      }
    });

    setcurrentQuestionIndex(questionsFromDatabase.length);
  };

  const submitAgain = () => {
    currentGroup.indices.forEach((idx) => {
      const id = questionsFromDatabase[idx].id;
      if (!(id in userAnswers)) {
        setUserAnswers((prev) => ({ ...prev, [id]: "" }));
      }
    });

    setOpenWarning(false);
    setcurrentQuestionIndex(questionsFromDatabase.length);
  };

  const handleBack = () => {
    if (currentGroupIdx > 0) {
      setcurrentQuestionIndex(groups[currentGroupIdx - 1].indices[0]);
    }
  };

  const insertSymbol = (symbol) => {
    const currentQuestion = questionsFromDatabase[currentQuestionIndex];
    const currentValue = userAnswers[currentQuestion.id] || '';
    const input = inputRef.current;

    if (input) {
      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? currentValue.length;
      const newValue = currentValue.slice(0, start) + symbol + currentValue.slice(end);
      handleAnswer(currentQuestion.id, newValue);
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(start + symbol.length, start + symbol.length);
          inputRef.current.focus();
        }
      });
    } else {
      handleAnswer(currentQuestion.id, currentValue + symbol);
    }
  };

  const renderInputForQuestionType = (questionType, questionOverride) => {
    const currentQuestion = questionOverride ?? questionsFromDatabase[currentQuestionIndex];

    if (questionType === 'multiple_choice') {
      return (
        <div className="w-full flex flex-col items-center justify-center">
          <div className="w-full max-w-3xl px-3 sm:px-6">
            {(currentQuestion.options ?? []).map((option, index) => (
              <Button
                key={index}
                variant="outlined"
                onClick={() => {
                  handleAnswer(currentQuestion.id, option);
                }}
                className={`${userAnswers[currentQuestion.id] === option ? classes.buttonSelect : classes.button}`}
                style={
                  currentQuestion.answers?.[0]?.case_sensitive
                    ? { textTransform: 'none' }
                    : undefined
                }
              >
                {option}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    if (questionType === 'text_input') {
      // Only an extended-response question gets the large writing area. A one-mark
      // answer is a word or a phrase, and a big empty box overstates what's wanted.
      const marks = Number(questionOverride?.max_marks ?? currentQuestion?.max_marks ?? 1);

      if (marks > 1) {
        return (
          <div className="w-full flex flex-col items-center px-4">
            <div className="w-full max-w-3xl">
              <div className="mb-1.5 flex items-baseline justify-between text-xs text-white/40">
                <span>Your answer</span>
                <span>{marks} Marks</span>
              </div>
              <textarea
                ref={answerBoxRef}
                rows={3}
                value={userAnswers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                placeholder="Write your answer in full sentences…"
                className="w-full resize-none overflow-hidden rounded-lg border border-white/20 bg-white/10 px-4 py-4 text-white text-base font-light leading-relaxed outline-none placeholder-white/25 focus:border-white/40 focus:bg-white/15 transition-colors"
                style={{ boxShadow: 'none' }}
              />
            </div>
          </div>
        );
      }

      return (
        <div className="w-full flex flex-col items-center px-4">
          <div className="w-full max-w-sm">
            <input
              ref={inputRef}
              type="text"
              value={userAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
              placeholder="Your answer"
              className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-5 text-white text-base font-light outline-none placeholder-white/25 focus:border-white/40 focus:bg-white/15 transition-colors"
              style={{ boxShadow: 'none' }}
            />
          </div>
        </div>
      );
    }

    if (questionType === 'numeric_input') {
      return (
        <div className="w-full flex flex-col items-center px-4">
          <div className="w-full max-w-sm">
            <MathLiveField
              key={currentQuestion.id}
              ref={(el) => {
                if (el) {
                  mathFieldRefs.current[currentQuestion.id] = el;
                } else {
                  delete mathFieldRefs.current[currentQuestion.id];
                }
              }}
              value={userAnswers[currentQuestion.id] ?? ''}
              onChange={(val) => handleAnswer(currentQuestion.id, val)}
              disabled={false}
              borderColor="#6b7280"
            />
          </div>
          <div className="mt-2">
            <MathSymbolKeyboard
              onSymbolClick={(latex) =>
                mathFieldRefs.current[currentQuestion.id]?.insertLatex(latex)
              }
            />
          </div>
        </div>
      );
    }

    if (questionType === 'fill_in_the_blank') {
      const answers = currentQuestion.answers ?? [];
      const userArr = Array.isArray(userAnswers[currentQuestion.id])
        ? userAnswers[currentQuestion.id]
        : Array(answers.length).fill('');
      const firstBlankKey = answers.length > 0 ? `${currentQuestion.id}-0` : null;

      return (
        <div className="w-full flex flex-col items-center px-4 gap-3">
          <div className="flex flex-wrap gap-3 justify-center">
            {answers.map((_, i) => {
              const blankKey = `${currentQuestion.id}-${i}`;

              return (
                <div key={i} className="flex flex-col gap-1">
                  <span className="text-xs text-white/50 font-medium">Blank {i + 1}</span>
                  <div
                    className="w-36"
                    onMouseDown={() => { activeBlankKeyRef.current = blankKey; }}
                    onTouchStart={() => { activeBlankKeyRef.current = blankKey; }}
                    onFocusCapture={() => { activeBlankKeyRef.current = blankKey; }}
                  >
                    <MathLiveField
                      key={blankKey}
                      ref={(el) => {
                        if (el) {
                          blankFieldRefs.current[blankKey] = el;
                        } else {
                          delete blankFieldRefs.current[blankKey];
                          if (activeBlankKeyRef.current === blankKey) {
                            activeBlankKeyRef.current = null;
                          }
                        }
                      }}
                      value={userArr[i] ?? ''}
                      onChange={(val) => {
                        const next = [...userArr];
                        next[i] = val;
                        handleAnswer(currentQuestion.id, next);
                      }}
                      disabled={false}
                      borderColor="#6b7280"
                      onEnter={() => {
                        if (i < answers.length - 1) {
                          const nextBlankKey = `${currentQuestion.id}-${i + 1}`;
                          activeBlankKeyRef.current = nextBlankKey;
                          blankFieldRefs.current[nextBlankKey]?.focus?.();
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center w-full">
            <div className="w-fit max-w-full">
              <MathSymbolKeyboard
                onSymbolClick={(latex) => {
                  const activeKey = activeBlankKeyRef.current;
                  const targetKey =
                    activeKey && activeKey.startsWith(`${currentQuestion.id}-`)
                      ? activeKey
                      : firstBlankKey;

                  if (targetKey) {
                    blankFieldRefs.current[targetKey]?.insertLatex?.(latex);
                  }
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (questionType === 'essay') {
      return (
        <div className="w-full flex justify-center px-4">
          <TextField
            multiline
            label="Your Answer"
            variant="standard"
            value={userAnswers[currentQuestion.id] || ''}
            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
            style={{ width: '100%', maxWidth: 760 }}
            InputProps={{
              style: { color: 'white', borderBottom: '1px solid white' },
              disableUnderline: true,
            }}
            inputProps={{ style: { boxShadow: 'none', color: 'white' } }}
            InputLabelProps={{ style: { color: 'white', fontWeight: 100 } }}
          />
        </div>
      );
    }

    return null;
  };

  const renderQuizStep = () => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center text-white text-2xl">
          Loading questions...
        </div>
      );
    }

    if (questionsFromDatabase.length === 0) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center text-white px-6">
          <h2 className="text-3xl mb-4 text-center">No Questions Found</h2>
          <p className="text-lg mb-6 text-center">
            There are no questions available for the selected filter.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1d638c] px-6 py-3 rounded-md hover:bg-[#2e92b0] transition"
          >
            Go Back
          </button>
        </div>
      );
    }

    if (currentQuestionIndex < questionsFromDatabase.length) {
      const currentQuestion = questionsFromDatabase[currentQuestionIndex];

      return (
        <>
          {/* Top bar */}
          <div className="w-full flex">
            <div className="w-1/2">
              <div
                onClick={() => window.location.reload()}
                className="font-thin px-4 sm:px-6 py-3 sm:py-4 relative bg-[#1c394a] text-white hover:cursor-pointer"
              >
                <div className="text-left relative z-10">Restart Quiz</div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
              </div>
            </div>

            <div className="w-1/2">
              <div className="font-thin px-4 sm:px-6 py-3 sm:py-4 relative bg-[#1c394a] text-white hover:cursor-pointer">
                <div onClick={handleSubmit} className="text-right relative z-10">
                  Submit Quiz
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
              </div>
            </div>
          </div>

          <div className="w-full">
            {/* Progress indicator — counts by group, not flat question */}
            <div className="relative mt-6 sm:mt-8 px-3 sm:px-6">
              <div className="w-full max-w-2xl mx-auto">
                <div className="flex items-center justify-between text-white font-thin text-sm sm:text-base">
                  <span>Question {currentGroupIdx + 1} / {groups.length}</span>
                  <span>{Math.round(((currentGroupIdx + 1) / groups.length) * 100)}%</span>
                </div>
                <div className="mt-2 w-full h-2 rounded-full bg-white/30 overflow-hidden">
                  <div
                    className="h-full bg-[#1d638c] transition-all duration-300 ease-out"
                    style={{ width: `${((currentGroupIdx + 1) / groups.length) * 100}%` }}
                  />
                </div>
              </div>

              {studentId && currentQuestion?.id && (
                <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50">
                  <QuestionReportButton questionId={currentQuestion.id} studentId={studentId} />
                </div>
              )}
            </div>

            {/* Main content */}
            <div className="flex flex-col justify-center py-6 sm:py-8 px-4 sm:px-10 bg-opacity-50">

              {currentGroup.isMultiPart ? (
                /* ── Multi-part card ── */
                <div>
                  <h2 className="text-white text-2xl sm:text-3xl font-thin mb-4">
                    Question {currentGroupIdx + 1}
                    <span className="text-base text-white/50 font-thin ml-2">· Multi-part</span>
                  </h2>
                  <div className="rounded-xl border border-white/20 bg-white/5 overflow-hidden divide-y divide-white/10 text-base sm:text-lg font-thin text-white">
                    {(() => {
                      // Extract the shared preamble from the first sub-part so we can
                      // deduplicate — sub-parts that repeat the same preamble won't show it again.
                      const firstQ = questionsFromDatabase[currentGroup.indices[0]];
                      const firstParts = (firstQ?.content ?? '').split(/\n\n+/);
                      const sharedPreamble = firstParts.length > 1 ? firstParts.slice(0, -1).join('\n\n') : null;

                      return currentGroup.indices.map((flatIdx, subIdx) => {
                        const q = questionsFromDatabase[flatIdx];
                        const isFirst = subIdx === 0;
                        const subLabel = (() => {
                          const sub = (q.question_number ?? '').replace(new RegExp(`^${currentGroup.parent}`), '');
                          return sub.split('_').map((p) => `(${p})`).join('');
                        })();
                        const parts = (q.content ?? '').split(/\n\n+/);
                        const subPreamble = parts.length > 1 ? parts.slice(0, -1).join('\n\n') : null;
                        const equation = parts[parts.length - 1];
                        // Only show sub-preamble if it differs from the shared top preamble
                        const showSubPreamble = subPreamble && !isFirst && subPreamble.trim() !== sharedPreamble?.trim();

                        return (
                          <div key={flatIdx} className="px-4 sm:px-6 py-5 flex flex-col items-center">
                            <div className="w-full max-w-2xl">
                              {/* Shared preamble shown once above the first sub-part */}
                              {isFirst && sharedPreamble && (
                                <p className="mb-2 text-left">
                                  <MathRenderer content={sharedPreamble} />
                                </p>
                              )}
                              {/* Per-sub preamble shown only when it differs from the shared one */}
                              {showSubPreamble && (
                                <p className="mb-2 text-left">
                                  <MathRenderer content={subPreamble} />
                                </p>
                              )}
                              <div className="flex items-baseline justify-center gap-2">
                                <span className="shrink-0 text-sm font-semibold text-white/60 whitespace-nowrap">{subLabel}</span>
                                <MathRenderer content={equation} />
                              </div>
                              {q.has_image && (
                                <div className="mt-3">
                                  <QuestionImageDisplay questionId={q.id} />
                                </div>
                              )}
                              <div className="mt-3 flex justify-center">
                                {renderInputForQuestionType(q.question_type, q)}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                /* ── Single question ── */
                <div>
                  <h2 className="text-white text-2xl sm:text-3xl font-thin">
                    Question {currentGroupIdx + 1}
                  </h2>
                  <MathRenderer
                    content={currentQuestion.content}
                    className="text-white font-thin text-base sm:text-lg mt-2"
                  />
                  <div className="mt-4">
                    <QuestionImageDisplay questionId={currentQuestion.id} />
                  </div>
                  <div className="my-5 rounded-md">
                    {renderInputForQuestionType(currentQuestion.question_type)}
                  </div>
                </div>
              )}

              <div className="flex items-center w-full justify-center gap-3 sm:gap-4 mt-6">
                {currentGroupIdx !== 0 && (
                  <button
                    onClick={handleBack}
                    className="transition ease-in-out delay-80 bg-[#1d638c] rounded-md
                               px-4 sm:px-6 py-3 sm:py-2 text-white relative
                               w-full sm:w-[16%] max-w-xs"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
                    <span className="relative z-10">Back</span>
                  </button>
                )}

                {currentGroupIdx !== groups.length - 1 ? (
                  <button
                    onClick={handleNext}
                    className="transition ease-in-out delay-80 bg-[#1d638c] rounded-md
                               px-4 sm:px-6 py-3 sm:py-2 text-white relative
                               w-full sm:w-[16%] max-w-xs"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
                    <span className="relative z-10">Next</span>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="transition ease-in-out delay-80 bg-[#1d8c49] rounded-md
                               px-4 sm:px-6 py-3 sm:py-2 text-white relative
                               w-full sm:w-[16%] max-w-xs"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#1d8c49] via-[#80edab] to-[#1d8c49] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
                    <span className="relative z-10">Submit</span>
                  </button>
                )}
              </div>

              {/* Dialog */}
              <Dialog
                open={openWarning}
                keepMounted
                onClose={handleCloseWarning}
                aria-describedby="alert-dialog-slide-description"
              >
                <DialogTitle>{"Incomplete Submission"}</DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-slide-description">
                    You have unanswered questions. Are you sure you want to submit?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseWarning}>Go Back</Button>
                  <Button onClick={() => { submitAgain(); }}>Submit</Button>
                </DialogActions>
              </Dialog>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="min-h-screen h-full">
          <QuizComplete questionsFromDatabase={questionsFromDatabase} userAnswers={userAnswers} />
        </div>
      </>
    );
  };

  return <div>{renderQuizStep()}</div>;
};

export default QuizContent;